import {Component, EventEmitter, Input, OnInit, OnDestroy, Output, ViewChild} from '@angular/core';
import {NgModel} from "@angular/forms";
import {debounceTime, Observable, Subject, switchMap} from "rxjs";
import {SaveRepairService} from "../../services/save-repair.service";
import {ClaimFormValidationService} from "../../../../services/claim-form-validation.service";
import {PartGroupDto} from "../../../../../auto-generated-server-dtos/claim/repairs/part-group-dto";
import {ClaimDto} from "../../../../../auto-generated-server-dtos/claim/claim-dto";
import {PartDto} from "../../../../../auto-generated-server-dtos/claim/repairs/part-dto";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {v4 as uuidv4} from 'uuid';
import {DeleteRepairModalComponent} from "../delete-repair-modal/delete-repair-modal.component";
import Utils from 'src/app/helpers/utils';
import { RepairDto } from 'src/app/auto-generated-server-dtos/adjuster/claim/repairs/models/repair-dto';

@Component({
  selector: 'app-repair',
  templateUrl: './repair.component.html',
  styleUrls: ['./repair.component.scss']
})
export class RepairComponent implements OnInit, OnDestroy {
  @Input() public claim: ClaimDto | null = null;
  @Input() public repair!: RepairDto;
  @Input() public availablePartGroups: PartGroupDto[] = [];
  @Input() IsAdjusterView = false;
  @Input() public RepairAtTimeOfSubmission: RepairDto | null = null;
  @Output() totalUpdated = new EventEmitter();
  @Output() repairDeleted = new EventEmitter<RepairDto>();
  @Output() repairUpdated = new EventEmitter<PartDto>();

  private _saveRepair$: Subject<any>;
  public saving = false;
  public readonly saveRepair$: Observable<any>;
  public deleting = false;
  public part: PartDto | null = null;
  public laborRate: string | null = null;
  public preApprovalExplanation: {label: string, quantity: string, value: string}[] = [];

  @ViewChild('partTypeSelect', { static: false }) partTypeSelect!: NgModel;
  @ViewChild('partTypeNotListedCheckbox', { static: false }) partTypeNotListedCheckbox!: NgModel;
  @ViewChild('partQuantityInput', { static: false }) partQuantityInput!: NgModel;
  @ViewChild('partPriceInput', { static: false }) partPriceInput!: NgModel;
  @ViewChild('labourHoursInput', { static: false }) labourHoursInput!: NgModel;
  @ViewChild('labourPriceInput', { static: false }) labourPriceInput!: NgModel;
  private componentId = uuidv4();
  public isRepairItemValid = false;

  constructor(
    private SaveRepairService: SaveRepairService,
    private claimFormValidationService: ClaimFormValidationService,
    private ngbModal: NgbModal,
    private Utils: Utils,
  ) {
    this._saveRepair$ = new Subject();

    this.saveRepair$ = this._saveRepair$.pipe(
      debounceTime(2000),
      switchMap((repair: RepairDto): any => {
        return this.SaveRepairService.saveRepair(repair);
      }));

    this.saveRepair$.subscribe(() => {
      this.saving = false;
    });
  }

  ngOnInit(): void {
    this.calculateTotal(false);
    this.part = this.Utils.setPartType(this.repair, this.availablePartGroups);
    this.updateLaborRateText();

    if (this.repair.preApprovalExplanation) {
      this.preApprovalExplanation = this.repair.preApprovalExplanation.trim().split('\r\n').map((explanation: string) => {
        const split = explanation.split('|');
        if (split.length == 3) {
          return {label: split[0].trim(), quantity: split[1].trim(), value: split[2].trim()};
        }
        return {label: split[0].trim(), quantity: "", value: (split[1] ?? "").trim()};
      });
    }

    this.claimFormValidationService.formValidationEvent$.subscribe(() => {
      this.validateRepairItem();
    });
  }

  ngOnDestroy(): void {
    this.claimFormValidationService.updateValidationResult(this.componentId, true);
  }

  public calculateTotal(updateClaimTotal = true) {
    let partPriceQuantitySum: number = this.repair.partPrice || 0;
    if (this.repair.partQuantity !== null) {
      partPriceQuantitySum = partPriceQuantitySum * this.repair.partQuantity!
    }
    this.repair!.total =
      + partPriceQuantitySum +
      + (this.repair.labourPrice || 0) +
      + (this.repair.otherPrice || 0) +
      + (this.repair.subletPrice || 0);
    if (updateClaimTotal) {
      this.totalUpdated.emit();
    }
  }

  public openDeleteRepairModal() {
    const modalRef = this.ngbModal.open(DeleteRepairModalComponent);
    modalRef.componentInstance.repair = this.repair;
    modalRef.componentInstance.part = this.part;

    modalRef.result.then(
      (result) => {
        this.deleting = true;
        this.repairDeleted.emit(this.repair);
      }
    );
  }

  public onPartTypeChange() {
    this.saving = true;
    this.part = this.Utils.setPartType(this.repair, this.availablePartGroups);
    this.repairUpdated.emit(this.part!);
    this.SaveRepairService.saveRepair(this.repair!).subscribe(() => {
      this.saving = false;
    });
  }

  onPartTypeNotListedChange() {
    if (this.repair.isPartTypeNotListed) {
      this.saving = true;
      this.repair.partId = null;
      this.part = null;
      this.repairUpdated.emit(this.part!);
      this.SaveRepairService.saveRepair(this.repair!).subscribe(() => {
        this.saving = false;
      });
    }
  }

  public saveRepairDebounce() {
    this.saving = true;
    this._saveRepair$.next(this.repair);
  }

  public updateLaborRateText() {
    this.laborRate = this.repair?.labourPrice && this.repair.labourHours
      ? `$${(this.repair?.labourPrice / this.repair?.labourHours).toFixed(2)}/hour`
      : null;
  }

  public updateLaborCharge() {
    if (this.claim!.repairCentre.doorRate && this.repair.labourHours) {
      this.repair.labourPrice = Math.round(this.repair.labourHours * this.claim!.repairCentre.doorRate * 100) / 100;
    }
  }

  public approveRepairItem() {
    this.saving = true;
    this.repair.status = 'Approved';
    this.SaveRepairService.saveRepair(this.repair).subscribe(() => {
      this.saving = false;
    });
  }

  public denyRepairItem() {
    this.saving = true;
    this.repair.partPrice = 0;
    this.repair.labourPrice = 0;
    this.repair.subletPrice = 0;
    this.repair.otherPrice = 0;
    this.repair.total = 0;
    this.repair.status = 'Denied';
    this.SaveRepairService.saveRepair(this.repair).subscribe(() => {
      this.calculateTotal(true);
      this.saving = false;
    });
  }

  private validateRepairItem() {
    const validationInputs = [
      this.partTypeSelect,
      this.partTypeNotListedCheckbox,
      this.partQuantityInput,
      this.partPriceInput,
      this.labourHoursInput,
      this.labourPriceInput
    ];

    this.partTypeNotListedCheckbox.control.setErrors(this.repair.isPartTypeNotListed ? { 'notListed': true } : null);
    validationInputs.forEach((input) => {
      input.control.markAsTouched();
    });
    this.isRepairItemValid = !validationInputs.some(input => {
      return input.control.errors;
    });
    
    this.claimFormValidationService.updateValidationResult(this.componentId, this.isRepairItemValid);
  }
  
}